import {
  DndContext as DndKitContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  DragEndEvent,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import React, {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  cloneElement,
  createContext,
  useContext
} from 'react';
import { Row } from 'react-table';
import { CSS } from '@dnd-kit/utilities';

const DragAndDropContext = createContext(null);

function useDnDContext() {
  const ctx = useContext(DragAndDropContext);

  if (ctx === undefined) {
    throw new Error(
      'useDnDContext should be used inside `ReactTableDragAndDrop`'
    );
  }

  return ctx;
}

function DragAndDropContextProvider({ children, ...rest }) {
  return (
    <DragAndDropContext.Provider value={rest}>
      {children}
    </DragAndDropContext.Provider>
  );
}

interface InvoiceItem {
  itemId: string;
}

function DragAndDrop({
  children,
  getRowId,
  ...rest
}: PropsWithChildren<{
  onDragEnd: (e: DragEndEvent) => void;
  /* To get the `id` from the `cell.row.original` */
  getRowId: (original: InvoiceItem) => string;
}>) {
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DragAndDropContextProvider getRowId={getRowId}>
      <DndKitContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        sensors={sensors}
        {...rest}
      >
        {children}
      </DndKitContext>
    </DragAndDropContextProvider>
  );
}

function DragHandleController({
  cell,
  children
}: {
  cell: any;
  children: React.ReactElement;
}) {
  const { getRowId } = useDnDContext();
  const { attributes, listeners, isDragging } = useSortable({
    id: getRowId(cell.row.original)
  });

  return cloneElement(children, {
    ...attributes,
    ...listeners,
    className: isDragging ? 'cursor-grabbing' : 'cursor-grab'
  });
}

function DraggableRow({
  row,
  children,
  disableDragAndDrop
}: {
  row: Row<InvoiceItem>;
  disableDragAndDrop: boolean;
  children: (arg: {
    style: React.CSSProperties;
    ref: (node: HTMLElement) => void;
  }) => ReactNode;
}) {
  const { getRowId } = useDnDContext();
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: getRowId(row.original)
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    // transition: 'all 150ms ease',
    // transition: '250',
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
    cursor: !disableDragAndDrop && isDragging ? 'grabbing' : 'auto'
  };

  return children({ style, ref: setNodeRef });
}

function SortableContextBody({
  children,
  listOfIds
}: PropsWithChildren<{ listOfIds: Array<string> }>) {
  return (
    <SortableContext items={listOfIds} strategy={verticalListSortingStrategy}>
      {children}
    </SortableContext>
  );
}

DragAndDrop.DragHandleController = DragHandleController;
DragAndDrop.DraggableRow = DraggableRow;
DragAndDrop.SortableContextBody = SortableContextBody;

export { DragAndDrop as ReactTableDragAndDrop };
